import { c as _c } from "react/compiler-runtime";
import { TWELVE_HOURS_MS } from '@netpurpose/types';
import { valueIsDefined } from '@netpurpose/utils';
import { useQueries, useQuery } from '@tanstack/react-query';
import { pickBy } from 'lodash';
import { useMemo } from 'react';
import { GeneratedFactApi } from '../../GeneratedApi';
import type { GetEntityDashboardDataResponse, GetPortfolioDashboardDataResponse } from '../../generated/facts';
import { snakeToCamelKeys } from '../../utils';
import { standardiseImpactDataForCompare, transformEntityImpactData, transformPortfolioImpactData } from './utils';
export const PORTFOLIO_IMPACT_DATA_QUERY_CACHE_KEY = 'portfolioImpactData';
const ENTITY_IMPACT_DATA_QUERY_CACHE_KEY = 'entityImpactData';
export const usePortfolioImpactData = t0 => {
  const $ = _c(18);
  const {
    portfolioId,
    snapshotId,
    selectedMetricConfigId,
    hasSnapshotsAccess
  } = t0;
  let t1;
  let t2;
  if ($[0] !== hasSnapshotsAccess || $[1] !== portfolioId || $[2] !== selectedMetricConfigId || $[3] !== snapshotId) {
    t1 = [PORTFOLIO_IMPACT_DATA_QUERY_CACHE_KEY, portfolioId, snapshotId, selectedMetricConfigId, hasSnapshotsAccess];
    t2 = () => portfolioId && selectedMetricConfigId ? GeneratedFactApi.portfolios.getPortfolioDashboardData({
      path: {
        portfolio_id: portfolioId
      },
      query: {
        ...pickBy({
          snapshot_id: snapshotId
        }, valueIsDefined),
        metrics_config_id: selectedMetricConfigId
      },
      ...(hasSnapshotsAccess ? {
        headers: {
          "x-np-feature": ["snapshot-attribution"]
        }
      } : {})
    }) : undefined;
    $[0] = hasSnapshotsAccess;
    $[1] = portfolioId;
    $[2] = selectedMetricConfigId;
    $[3] = snapshotId;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  const t3 = !!portfolioId && !!selectedMetricConfigId;
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: TWELVE_HOURS_MS
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = useQuery(t4);
  let data;
  let rest;
  if ($[10] !== t5) {
    ({
      data,
      ...rest
    } = t5);
    $[10] = t5;
    $[11] = data;
    $[12] = rest;
  } else {
    data = $[11];
    rest = $[12];
  }
  let t6;
  if ($[13] !== data) {
    t6 = data ? data.map(_temp) : undefined;
    $[13] = data;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const transformedPortfolioImpactData = t6;
  let t7;
  if ($[15] !== rest || $[16] !== transformedPortfolioImpactData) {
    t7 = {
      ...rest,
      data: transformedPortfolioImpactData
    };
    $[15] = rest;
    $[16] = transformedPortfolioImpactData;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};
export type PortfolioImpactData = NonNullable<ReturnType<typeof usePortfolioImpactData>['data']>[number];
export const usePortfoliosImpactData = t0 => {
  const $ = _c(16);
  const {
    ids,
    selectedMetricConfigId,
    hasSnapshotsAccess
  } = t0;
  let t1;
  if ($[0] !== hasSnapshotsAccess || $[1] !== ids || $[2] !== selectedMetricConfigId) {
    let t2;
    if ($[4] !== hasSnapshotsAccess || $[5] !== selectedMetricConfigId) {
      t2 = t3 => {
        const {
          snapshotId,
          portfolioId
        } = t3;
        return {
          queryKey: [PORTFOLIO_IMPACT_DATA_QUERY_CACHE_KEY, portfolioId, snapshotId, selectedMetricConfigId],
          queryFn: () => selectedMetricConfigId ? GeneratedFactApi.portfolios.getPortfolioDashboardData({
            path: {
              portfolio_id: portfolioId
            },
            query: {
              metrics_config_id: selectedMetricConfigId,
              snapshot_id: snapshotId
            },
            ...(hasSnapshotsAccess ? {
              headers: {
                "x-np-feature": ["snapshot-attribution"]
              }
            } : {})
          }).then(impactData => [`${snapshotId}`, impactData] as [string, GetPortfolioDashboardDataResponse]) : undefined,
          enabled: !!snapshotId && !!portfolioId && !!selectedMetricConfigId,
          staleTime: TWELVE_HOURS_MS
        };
      };
      $[4] = hasSnapshotsAccess;
      $[5] = selectedMetricConfigId;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = ids.map(t2);
    $[0] = hasSnapshotsAccess;
    $[1] = ids;
    $[2] = selectedMetricConfigId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = {
      queries: t1
    };
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const response = useQueries(t2);
  let t3;
  if ($[9] !== response) {
    t3 = standardiseImpactDataForCompare({
      response
    });
    $[9] = response;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const standardisedImpactData = t3;
  let t4;
  if ($[11] !== response) {
    t4 = response.reduce(_temp2, false);
    $[11] = response;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const isFetching = t4;
  let t5;
  if ($[13] !== isFetching || $[14] !== standardisedImpactData) {
    t5 = {
      data: standardisedImpactData,
      isFetching
    };
    $[13] = isFetching;
    $[14] = standardisedImpactData;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
export const useEntityImpactData = t0 => {
  const $ = _c(16);
  const {
    entityId,
    selectedMetricConfigId
  } = t0;
  let t1;
  let t2;
  if ($[0] !== entityId || $[1] !== selectedMetricConfigId) {
    t1 = [ENTITY_IMPACT_DATA_QUERY_CACHE_KEY, entityId, selectedMetricConfigId];
    t2 = () => entityId && selectedMetricConfigId ? GeneratedFactApi.entities.getEntityDashboardData({
      path: {
        entity_id: entityId
      },
      query: {
        metrics_config_id: selectedMetricConfigId
      }
    }) : undefined;
    $[0] = entityId;
    $[1] = selectedMetricConfigId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = !!entityId && !!selectedMetricConfigId;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = useQuery(t4);
  let data;
  let rest;
  if ($[8] !== t5) {
    ({
      data,
      ...rest
    } = t5);
    $[8] = t5;
    $[9] = data;
    $[10] = rest;
  } else {
    data = $[9];
    rest = $[10];
  }
  let t6;
  if ($[11] !== data) {
    t6 = data ? data.map(_temp3) : undefined;
    $[11] = data;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const transformedEntityImpactData = t6;
  let t7;
  if ($[13] !== rest || $[14] !== transformedEntityImpactData) {
    t7 = {
      ...rest,
      data: transformedEntityImpactData
    };
    $[13] = rest;
    $[14] = transformedEntityImpactData;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
export type EntityImpactData = NonNullable<ReturnType<typeof useEntityImpactData>['data']>[number];
export type FactoidOrigin = NonNullable<EntityImpactData['factoidOrigin']>;
export const useEntitiesImpactData = t0 => {
  const $ = _c(15);
  const {
    entityIds,
    selectedMetricConfigId
  } = t0;
  let t1;
  if ($[0] !== entityIds || $[1] !== selectedMetricConfigId) {
    let t2;
    if ($[3] !== entityIds.length || $[4] !== selectedMetricConfigId) {
      t2 = entityId => ({
        queryKey: [ENTITY_IMPACT_DATA_QUERY_CACHE_KEY, entityId, selectedMetricConfigId],
        queryFn: () => selectedMetricConfigId ? GeneratedFactApi.entities.getEntityDashboardData({
          path: {
            entity_id: parseInt(entityId, 10)
          },
          query: {
            metrics_config_id: selectedMetricConfigId
          }
        }).then(impactData => [entityId, impactData] as [string, GetEntityDashboardDataResponse]) : undefined,
        enabled: !!entityIds.length && !!selectedMetricConfigId,
        staleTime: TWELVE_HOURS_MS
      });
      $[3] = entityIds.length;
      $[4] = selectedMetricConfigId;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    t1 = entityIds.map(t2);
    $[0] = entityIds;
    $[1] = selectedMetricConfigId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = {
      queries: t1
    };
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const response = useQueries(t2);
  let t3;
  if ($[8] !== response) {
    t3 = standardiseImpactDataForCompare({
      response
    });
    $[8] = response;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const standardisedImpactData = t3;
  let t4;
  if ($[10] !== response) {
    t4 = response.reduce(_temp4, false);
    $[10] = response;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const isFetching = t4;
  let t5;
  if ($[12] !== isFetching || $[13] !== standardisedImpactData) {
    t5 = {
      data: standardisedImpactData,
      isFetching
    };
    $[12] = isFetching;
    $[13] = standardisedImpactData;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
function _temp(d) {
  return transformPortfolioImpactData(snakeToCamelKeys(d));
}
function _temp2(acc, curr) {
  return acc || curr.isFetching;
}
function _temp3(d) {
  return transformEntityImpactData(snakeToCamelKeys(d));
}
function _temp4(acc, curr) {
  return acc || curr.isFetching;
}